import {auth} from '@common/auth/use-auth';
import {openBeforeEmailVerified} from '@common/auth/verify-email-paths';
import {getSafeInternalPath} from '@common/auth/get-safe-redirect-path';
import {getBootstrapData} from '@ui/bootstrap-data/bootstrap-data-store';

/** Signed in, confirmation is on, email not confirmed, not an admin "View as". */
export function needsEmailVerification(): boolean {
  const data = getBootstrapData();
  return Boolean(
    auth.user &&
      data.settings?.require_email_confirmation &&
      !auth.user.email_verified_at &&
      !data.impersonator,
  );
}

export function mustVerifyEmailFirst(pathname: string): boolean {
  if (!needsEmailVerification()) {
    return false;
  }
  return !openBeforeEmailVerified(
    pathname,
    getBootstrapData().settings?.html_base_uri,
  );
}

/** /verify-email that returns to `next` once the code is confirmed. */
export function verifyEmailUrl(next?: string | null): string {
  const safe = nextAfterVerify(next);
  return safe ? `/verify-email?next=${encodeURIComponent(safe)}` : '/verify-email';
}

export function nextAfterVerify(next?: string | null): string | null {
  const path = getSafeInternalPath(next ?? undefined);
  if (!path || path === '/' || path.startsWith('/verify-email')) {
    return null;
  }
  return path;
}

/**
 * Where to go after confirming: the page they were trying to open, else
 * pricing when they have no plan yet, else the normal home page.
 */
export function destinationAfterVerify(
  next: string | null | undefined,
  needsPlan: boolean,
): string {
  const explicit = nextAfterVerify(next);
  const home = getBootstrapData().auth_redirect_uri || '/drive';
  if (explicit && explicit !== home) {
    return explicit;
  }
  if (needsPlan) {
    return '/pricing';
  }
  return explicit ?? auth.getRedirectUri() ?? home;
}

export {maskEmailAddress} from '@common/auth/mask-email';
